// Thin LOCAL fetch client for the THU-346 (Slice 2) clinical-facts endpoints — the Info column's
// editable conditions / allergies / medications, persisted to the FB-authorized synthetic demo clinical
// store. Deliberately NOT in the published SDK yet (local-first). Mirrors the SDK HttpClient shape:
// baseUrl + getToken → Bearer, credentials:"include" (dev cookie), unwraps the { data } envelope.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export type FactKind = "condition" | "allergy" | "medication";

export interface ClinicalFact {
  id: string;
  kind: FactKind;
  name: string;
  notes: string | null;
  created_at?: string;
}
export interface ClinicalFactInput {
  kind: FactKind;
  name: string;
  notes?: string;
}

export interface ClinicalFactsApi {
  list(patientId: string): Promise<ClinicalFact[]>;
  create(patientId: string, fact: ClinicalFactInput): Promise<ClinicalFact>;
  remove(patientId: string, factId: string): Promise<void>;
}

export function useClinicalFactsApi(): ClinicalFactsApi {
  const { getToken } = useAppAuth();
  return useMemo<ClinicalFactsApi>(() => {
    async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { code?: string; message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    const base = (patientId: string) =>
      `/v1/doctor/patients/${encodeURIComponent(patientId)}/clinical-facts`;
    return {
      list: (patientId) => request<ClinicalFact[]>("GET", base(patientId)),
      create: (patientId, fact) => request<ClinicalFact>("POST", base(patientId), fact),
      remove: (patientId, factId) =>
        request<void>("DELETE", `${base(patientId)}/${encodeURIComponent(factId)}`),
    };
  }, [getToken]);
}
